<template>
    <div class="hidden lg:flex lg:flex-col lg:w-[477px] bg-black border-l border-[#2e2e2e] h-screen overflow-hidden">
        <!-- Top Bar -->
        <div class="px-3 py-3 bg-black flex items-center justify-between shrink-0 border-b border-white/5 gap-2">
            <a
                :href="homeFeedUrl"
                aria-label="Back to clips feed"
                class="relative z-[20] flex items-center gap-2 min-w-0"
            >
                <!-- Decorative: the adjacent text already names the section. Intrinsic
                     dimensions are declared so the reserved box is right on first paint. -->
                <img
                    src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png"
                    alt=""
                    width="418"
                    height="385"
                    loading="eager"
                    class="h-4 w-auto shrink-0"
                />
                <p class="text-white/80 text-[0.65rem] xl:text-xs font-semibold tracking-wide uppercase whitespace-nowrap truncate">
                    {{ filter?.label ?? 'Naughty America' }}
                </p>
            </a>

            <div class="flex items-center gap-2 shrink-0">
                <button
                    v-if="!isUserLoggedIn"
                    type="button"
                    @click="goToJoin"
                    class="relative z-[20] bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold px-3 xl:px-4 py-1.5 xl:py-2 rounded-lg text-[0.6rem] xl:text-[0.65rem] transition-all shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 whitespace-nowrap"
                >
                    JOIN
                </button>

                <ClipsKebabMenu
                    :isUserLoggedIn="isUserLoggedIn"
                    :routePrefix="routePrefix"
:currentClip="currentClip"
                />
            </div>
        </div>

        <!-- Main Content Area - Scrollable -->
        <div class="flex-1 bg-[#141414] px-8 xl:px-12 overflow-y-auto min-h-0 custom-scrollbar">
            <!-- Description of the clip on screen - moved off the video overlay, so it
                 follows currentClip as the user swipes. Desktop only by construction:
                 this whole column is hidden below lg. -->
            <p v-if="currentClip?.description" class="text-white/80 text-sm font-normal leading-relaxed pt-6">
                {{ currentClip.description }}
            </p>

            <!-- Join CTA - tour/guests only, members never see it -->
            <div v-if="!isUserLoggedIn" class="py-6">
                <button
                    type="button"
                    @click="goToJoinNow"
                    class="w-full bg-auditionsBlue hover:bg-auditionsBlueHover active:bg-auditionsBlueHover text-white font-bold py-3 rounded-lg text-sm uppercase tracking-wide transition-colors shadow-lg shadow-auditionsBlue/25"
                >
                    Join Now
                </button>
            </div>

            <!-- More Clips Grid -->
            <div v-if="moreClips.length > 0" class="py-6">
                <h3 class="text-white font-bold text-sm mb-4">
                    More Clips
                </h3>
                <div class="grid grid-cols-2 xl:grid-cols-3 gap-3">
                    <ClipTile v-for="clip in moreClips" :key="clip.id" :clip="clip" :href="clipFeedUrl(clip.slug)" />
                </div>
            </div>

            <!-- Footer Links -->
            <LegalFooterLinks />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import LegalFooterLinks from '@/Components/Common/LegalFooterLinks.vue'
import ClipsKebabMenu from '@/Components/VerticalClips/ClipsKebabMenu.vue'
import ClipTile from '@/Components/VerticalClips/ClipTile.vue'
import { useClipAnalytics } from '@/composables/useClipAnalytics'

const props = defineProps({
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    moreClips: {
        type: Array,
        default: () => []
    },
    // The formatClip() payload of the clip currently on screen, or null before the
    // first slide settles. Supplies the description and the per-clip join attribution.
    currentClip: {
        type: Object,
        default: null
    },
    // { type, slug, label } on a performer- or fantasy-filtered feed, null otherwise.
    // Only the label is used here - it replaces the section title.
    filter: {
        type: Object,
        default: null
    },
    // The grid is fed by the same partition the page is on, so its links have to point
    // back into that partition's route family.
    isSfwFeed: {
        type: Boolean,
        default: false
    },
})

const clipFeedUrl = (slug) =>
    route(props.routePrefix + '.verticalClips.' + (props.isSfwFeed ? 'sfw.' : '') + 'feed', {
        clip: slug,
    })

// Star + title click convention: back to the unfiltered feed's start, same partition.
const homeFeedUrl = computed(() =>
    route(props.routePrefix + '.verticalClips.' + (props.isSfwFeed ? 'sfw.' : '') + 'feed')
)

const { trackJoinCtaClicked } = useClipAnalytics({ routePrefix: props.routePrefix })

// custom7 carries the clip slug so the per-clip attribution the retired pricing island
// used to send survives the move to a single CTA.
const goToJoinNow = () => {
    const destination = route('www.join.signUp', {
        custom6: 'clipsSidebarJoinNow',
        ...(props.currentClip?.slug ? { custom7: props.currentClip.slug } : {}),
    })

    trackJoinCtaClicked('clipsSidebarJoinNow', props.currentClip ?? null, destination)

    window.location.href = destination
}

const goToJoin = () => {
    const destination = route('www.join.signUp', {
        custom6: 'clipsSidebarJoin',
        ...(props.currentClip?.slug ? { custom7: props.currentClip.slug } : {}),
    })

    trackJoinCtaClicked('clipsSidebarJoin', props.currentClip ?? null, destination)

    window.location.href = destination
}
</script>

<style scoped>
.custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    transition: background-color 0.2s ease;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.25);
}

.custom-scrollbar::-webkit-scrollbar-thumb:active {
    background-color: rgba(255, 255, 255, 0.35);
}
</style>
